import { apiClient } from '@shared/api';
import type { ApiResponse, PagedResult } from '@shared/types';
import type {
  SalesOrderDto,
  SalesOrderListItemDto,
  SalesOrderListParams,
  SalesOrderPriceCorrectionResult,
  SaveSalesOrderDraftRequest,
  SubmitSalesOrderRequest,
} from '../types/salesOrder.types';

// FO-SOI-3: Singleton service that wraps every /api/sales-orders/* call.
class SalesOrderService {
  private readonly baseUrl = '/sales-orders';

  async getPaged(params: SalesOrderListParams = {}): Promise<PagedResult<SalesOrderListItemDto>> {
    const { data } = await apiClient.get<ApiResponse<PagedResult<SalesOrderListItemDto>>>(
      `${this.baseUrl}/paged`,
      // 86d3bujyr: `indexes: null` serializes `statuses` as repeated `?statuses=0&statuses=2`
      // (no `[]`), which ASP.NET's [FromQuery] SalesOrderStatusEnum[] binds. Axios default
      // would emit `statuses[]=…` and silently bind nothing.
      { params, paramsSerializer: { indexes: null } },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load sales orders');
  }

  async getById(id: number): Promise<SalesOrderDto> {
    const { data } = await apiClient.get<ApiResponse<SalesOrderDto>>(`${this.baseUrl}/${id}`);
    if (data.success && data.data) return data.data;
    throw new Error(data.message || `Sales order ${id} not found`);
  }

  // FO-SOI-1 row-splice projection — called by the SSE handler.
  async getRow(id: number): Promise<SalesOrderListItemDto> {
    const { data } = await apiClient.get<ApiResponse<SalesOrderListItemDto>>(
      `${this.baseUrl}/${id}/row`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || `Sales order ${id} row not found`);
  }

  async saveDraft(request: SaveSalesOrderDraftRequest): Promise<SalesOrderDto> {
    const { data } = await apiClient.post<ApiResponse<SalesOrderDto>>(
      `${this.baseUrl}/draft`,
      request,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to save draft');
  }

  // 86d3ah33r async posting: this only CLAIMS the draft (Draft/PostingFailed -> Posting) and
  // returns 202 with no payload. The slow NetSuite post runs in the background worker; the
  // result (Submitted / PostingFailed) arrives via SSE. Resolves on a successful claim, throws
  // on a rejected claim (validation / not found / not postable). No timeout override needed —
  // the claim is fast (no NetSuite call), which is the whole point of going async.
  async submit(id: number, request: SubmitSalesOrderRequest): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `${this.baseUrl}/${id}/submit`,
      request,
    );
    if (!data.success) throw new Error(data.message || 'Submit failed');
  }

  // 86d3mnuxe funnel — close a NotReached-failed posting (Failed → Cancelled) so the draft accepts a
  // fresh submit. The BE refuses (400) when the post may have reached NetSuite (reconcile instead).
  async cancelPosting(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(`${this.baseUrl}/${id}/cancel-posting`);
    if (!data.success) throw new Error(data.message || 'Failed to cancel the posting');
  }

  // 86d3mnuxe funnel — Reconcile a REACHED failure: the BE re-arms the same job (frozen decision) so
  // the adopt recovers the Sales Order NetSuite already accepted. Never re-decides, never prompts.
  async reconcilePosting(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(`${this.baseUrl}/${id}/reconcile-posting`);
    if (!data.success) throw new Error(data.message || 'Failed to start the reconcile');
  }

  async archive(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(`${this.baseUrl}/${id}/archive`);
    if (!data.success) throw new Error(data.message || 'Archive failed');
  }

  async restore(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(`${this.baseUrl}/${id}/restore`);
    if (!data.success) throw new Error(data.message || 'Restore failed');
  }

  async delete(id: number): Promise<void> {
    const { data } = await apiClient.delete<ApiResponse<unknown>>(`${this.baseUrl}/${id}`);
    if (!data.success) throw new Error(data.message || 'Delete failed');
  }

  // 86d3gdj5q — one-time "Correct Price" tool. Re-rounds each line's VAT-inclusive unit to the
  // centavo and pushes the corrected ex-VAT rate to the already-posted NetSuite SO so it ties to
  // the customer PO, then syncs the DOP snapshot. The endpoint PATCHes lines sequentially and
  // reads the order back to verify — it can take ~2 min on a many-line SO, hence the long timeout.
  // Returns the result (which carries its own success/message); throws only on a request-level
  // failure (404 / not permitted).
  async correctPrice(id: number): Promise<SalesOrderPriceCorrectionResult> {
    const { data } = await apiClient.post<ApiResponse<SalesOrderPriceCorrectionResult>>(
      `${this.baseUrl}/${id}/correct-price`,
      undefined,
      { timeout: 180_000 },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Price correction failed');
  }
}

export const salesOrderService = new SalesOrderService();
